/* Hallmark · genre: editorial · macrostructure: Workbench (portfolio command center)
 * variation: factual portfolio strip + date-led workspaces + split first-run path
 * design-system: design.md · designed-as-app · tone: quiet luxury with operational clarity
 * pre-emit critique: P5 H5 E5 S5 R5 V4 · contrast: pass (40–41) · slop: pass (58/58)
 */
import React from "react";
import { useContent, useSetFlash, useSuperglue } from "@thoughtbot/superglue";
import {
  CheckIcon,
  CopyIcon,
  ExternalLinkIcon,
  EyeIcon,
  HeartIcon,
  MessageCircleIcon,
  PaletteIcon,
  PencilIcon,
  UsersIcon,
} from "lucide-react";

import {
  Form,
  type FormProps,
  type RailsHiddenFieldProps,
} from "@javascript/components/Inputs";
import { SetupProgressRail, type SetupStep } from "@javascript/components/SetupProgressRail";
import { Badge } from "@javascript/components/ui/badge";
import { Button } from "@javascript/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@javascript/components/ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@javascript/components/ui/input-group";
import { Separator } from "@javascript/components/ui/separator";
import { Switch } from "@javascript/components/ui/switch";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@javascript/components/ui/empty";
import { TypographyH1, TypographyMuted } from "@javascript/components/ui/typography";
import { cn } from "@javascript/components/utils";

// One way to pay, already written the way the buyer reads it. `available` is
// false only while nobody is switched on to take money, and then it is false
// for every option at once.
type CheckoutOption = {
  value: string;
  label: string;
  merchant: string;
  merchantMarkPath: string | null;
  methodsSummary: string;
  marks: { name: string; path: string }[];
  note: string | null;
  available: boolean;
  path: string;
};

type Invitation = {
  coupleNames: string;
  weddingDate?: string;
  themePreviewUrl: string;
  editPath: string;
  guestsPath: string;
  attendanceSummary: {
    responseCount: number;
    attendingCount: number;
    notAttendingCount: number;
    expectedHeadcount: number;
  };
  publicUrl: string;
  published: boolean;
  readyToPublish: boolean;
  liveTransaction: boolean;
  transactionInProgress: boolean;
  paidAndReady: boolean;
  lastTransactionFailed: boolean;
  checkoutPath: string;
  checkoutOptions: CheckoutOption[];
  setupProgress?: {
    steps: SetupStep[];
    progressValue: number;
    progressLabel: string;
    nextStep: { key: string; label: string };
    continueSetupPath: string;
  };
  previewPath: string;
  togglePublicationForm: FormProps<{
    published: RailsHiddenFieldProps;
    submit: { text: string };
  }>;
  changeThemePath: string;
  whatsappShareUrl: string;
};

type DashboardContent = {
  invitations: Invitation[];
  dashboardSummary: {
    invitationCount: number;
    publishedCount: number;
    responseCount: number;
    expectedHeadcount: number;
  };
  newInvitationPath: string;
  demoInvitationPath: string;
  confirmationPending: boolean;
  confirmationPath: string;
  price?: string;
  welcome?: boolean;
};

const firstRunSteps = [
  {
    title: "Choose a theme",
    description: "Start with a curated theme made for weddings.",
  },
  {
    title: "Add your details",
    description: "Build the schedule, story, wedding party, gallery, and guest guide.",
  },
  {
    title: "Publish one link",
    description: "Share a mobile-first invitation where guests can RSVP.",
  },
];

const CHECKOUT_ERROR_FALLBACK = "Checkout could not be opened. Please try again.";

// Shared by the paywall dialog's "Continue to pay" and a card's "Resume
// checkout": both just start (or reuse) the same pending transaction and hand the
// browser off to wherever the server says the buyer pays — our own overlay page
// for one provider, the provider's hosted page for another. Which it is has
// already been decided in Ruby. Each call site gets its own pending flag.
function useCheckoutAction() {
  const { csrfToken } = useSuperglue();
  const { setFlash } = useSetFlash();
  const [pending, setPending] = React.useState(false);

  const start = React.useCallback(
    async (checkoutPath: string) => {
      setPending(true);

      try {
        const response = await fetch(checkoutPath, {
          method: "POST",
          headers: { Accept: "application/json", "X-CSRF-Token": csrfToken ?? "" },
        });
        const body = await response.json();

        if (!response.ok || typeof body.checkoutUrl !== "string") {
          setFlash({ error: body.error ?? CHECKOUT_ERROR_FALLBACK });
          setPending(false);
          return;
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "initiate_checkout" });

        window.location.assign(body.checkoutUrl);
      } catch {
        setFlash({ error: CHECKOUT_ERROR_FALLBACK });
        setPending(false);
      }
    },
    [csrfToken, setFlash],
  );

  return { pending, start };
}

function PaywallDialog({
  invitation,
  open,
  onOpenChange,
}: {
  invitation: Invitation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { pending, start } = useCheckoutAction();
  // Read here rather than drilled from the page: the price belongs to the
  // catalog, not to the invitation this dialog was opened for.
  const { price } = useContent<DashboardContent>();
  const options = invitation.checkoutOptions;
  const groupId = React.useId();
  const [chosenValue, setChosenValue] = React.useState(
    () => options.find((option) => option.available)?.value,
  );
  // Nothing selectable means nothing to continue to, so the footer's one action
  // reads its own state off the choice instead of re-deriving availability.
  const chosen = options.find((option) => option.value === chosenValue && option.available);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Wider than the default dialog: this one is a comparison, not a
          confirmation, and two cards carrying wordmarks and a row of card
          badges need the room to be recognised at a glance. */}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>One payment for one invitation</DialogTitle>
          <DialogDescription>
            Publishing <strong className="font-medium text-foreground">{invitation.coupleNames}</strong>{" "}
            costs a one-time payment{price ? <> of <strong className="font-medium text-foreground">{price}</strong></> : null},
            charged once. Unpublishing it and publishing it again later costs nothing — you only ever
            pay once.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="min-w-0">
          <legend className="text-sm font-medium text-foreground">Choose how to pay</legend>
          <div className="mt-3 space-y-2">
            {options.map((option) => (
              <CheckoutChoice
                key={option.value}
                option={option}
                groupId={groupId}
                chosen={option.value === chosen?.value}
                onChoose={() => setChosenValue(option.value)}
              />
            ))}
          </div>
        </fieldset>
        {!chosen && (
          <p className="text-sm text-muted-foreground">
            Payment is unavailable right now, so this invitation cannot be published yet. Write to{" "}
            <a
              className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
              href="mailto:contact@goldypaper.com"
            >
              contact@goldypaper.com
            </a>{" "}
            and we will sort it out.
          </p>
        )}
        <a
          href="/terms#refunds"
          target="_blank"
          rel="noreferrer"
          className="text-sm font-medium text-foreground underline underline-offset-4 hover:text-primary"
        >
          Read the refund policy
        </a>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Not now
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={pending || !chosen}
            onClick={() => chosen && start(chosen.path)}
          >
            {pending ? "Opening checkout…" : "Continue to pay"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// A light plaque to sit a merchant's wordmark on. Both wordmarks are published
// in a dark variant only — and may not be recoloured — so the chip is what keeps
// them legible against a dialog that is nearly black in dark mode. It costs
// nothing in light mode, where it reads as no chip at all.
//
// The card badges need none of this: each carries its own background.
function BrandChip({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-flex h-8 shrink-0 items-center justify-center rounded-sm bg-white px-2", className)}
    >
      {children}
    </span>
  );
}

// The same shape whether or not it can be picked, because the buyer reads the
// card for two things the label alone cannot hold: what they can pay with, and
// who will be charging them.
//
// The marks are decorative — `methodsSummary` says the same thing in words, so a
// screen reader is not read a list of logos, and artwork that fails to load
// costs nothing.
function CheckoutChoice({
  option,
  groupId,
  chosen,
  onChoose,
}: {
  option: CheckoutOption;
  groupId: string;
  chosen: boolean;
  onChoose: () => void;
}) {
  const titleId = `${groupId}-${option.value}-title`;
  const detailId = `${groupId}-${option.value}-detail`;

  return (
    <label
      data-checkout-option={option.value}
      className={cn(
        "flex min-w-0 gap-3 rounded-lg border bg-background p-3 transition-colors",
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50",
        option.available
          ? "cursor-pointer hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:ring-2 has-[:checked]:ring-primary/25"
          : "cursor-not-allowed opacity-60",
      )}
    >
      <input
        type="radio"
        name={groupId}
        value={option.value}
        checked={chosen}
        disabled={!option.available}
        onChange={onChoose}
        aria-labelledby={titleId}
        aria-describedby={detailId}
        className="mt-0.5 size-4 shrink-0 accent-primary"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex flex-wrap items-center justify-between gap-2">
          <span id={titleId} className="text-sm font-medium text-foreground">
            {option.label}
          </span>
          {!option.available && <Badge variant="outline">Temporarily unavailable</Badge>}
        </span>
        {/* A row of its own, not sharing one with the sentence: otherwise a
            provider accepting three methods wraps where one accepting two does
            not, and two cards side by side stop looking like the same thing. */}
        {option.marks.length > 0 && (
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            {option.marks.map((mark) => (
              // The ring is for PayPal, whose badge is white: without an edge it
              // dissolves into a light-mode card.
              <img
                key={mark.name}
                data-payment-mark={mark.name}
                src={mark.path}
                alt=""
                aria-hidden="true"
                className="h-6 w-auto shrink-0 rounded-[3px] ring-1 ring-black/10"
              />
            ))}
          </span>
        )}
        <span className="text-sm text-muted-foreground">{option.methodsSummary}</span>
        {/* Wrapping rather than a breakpoint: the sentence drops below the
            wordmark exactly when the card is too narrow to hold both, which is
            a question about this box and not about the window. */}
        <span id={detailId} className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {option.merchantMarkPath && (
            <BrandChip>
              <img data-merchant-mark={option.merchant} src={option.merchantMarkPath} alt="" className="h-5 w-auto" />
            </BrandChip>
          )}
          <span className="min-w-44 flex-1">
            Charged by {option.merchant} — this name may appear on your statement.
            {option.note ? ` ${option.note}` : ""}
          </span>
        </span>
      </span>
    </label>
  );
}

function DashboardSummary({ summary }: { summary: DashboardContent["dashboardSummary"] }) {
  const metrics = [
    { label: "Invitations", value: summary.invitationCount },
    { label: "Live", value: summary.publishedCount },
    { label: "Replies", value: summary.responseCount },
    { label: "Expected so far", value: summary.expectedHeadcount },
  ];

  return (
    <section aria-label="Invitation portfolio" data-dashboard-summary className="mt-8 border-y">
      <dl className="grid grid-cols-2 sm:grid-cols-4">
        {metrics.map((metric, index) => (
          <div
            key={metric.label}
            className={cn(
              "min-w-0 px-4 py-4 sm:px-5",
              index % 2 === 1 && "border-l",
              index >= 2 && "border-t sm:border-t-0",
              index >= 1 && "sm:border-l",
            )}
          >
            <dt className="text-xs font-medium text-muted-foreground">{metric.label}</dt>
            <dd className="mt-1 font-serif text-2xl font-semibold tabular-nums text-foreground">
              {metric.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CreateInvitationAction({
  confirmationPending,
  confirmationPath,
  newInvitationPath,
}: Pick<DashboardContent, "confirmationPending" | "confirmationPath" | "newInvitationPath">) {
  if (!confirmationPending) {
    return (
      <Button asChild>
        <a href={newInvitationPath} data-sg-visit="true" aria-label="Create invitation">
          Create invitation
        </a>
      </Button>
    );
  }

  return (
    <div className="space-y-2">
      <Button disabled>Create invitation</Button>
      <p className="text-sm text-muted-foreground">
        Confirm your email address before creating an invitation. {" "}
        <a
          href={confirmationPath}
          data-sg-visit="true"
          className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
        >
          Resend confirmation
        </a>
      </p>
    </div>
  );
}

// Paying and going live are separate acts, so an invitation that is bought and
// waiting must not wear the same badge as one nobody has paid for.
function publicationStatusOf({
  published,
  settlingTransaction,
  paidAndReady,
}: {
  published: boolean;
  settlingTransaction: boolean;
  paidAndReady: boolean;
}): { variant: React.ComponentProps<typeof Badge>["variant"]; label: string } {
  if (published) return { variant: "success", label: "Published" };
  if (settlingTransaction) return { variant: "outline", label: "Payment in progress" };
  if (paidAndReady) return { variant: "default", label: "Paid · not published" };

  return { variant: "secondary", label: "Unpublished" };
}

function InvitationWorkspace({
  invitation,
  copied,
  onCopy,
}: {
  invitation: Invitation;
  copied: boolean;
  onCopy: (url: string) => Promise<void>;
}) {
  const setupProgress = invitation.setupProgress;
  const needsAttention = !invitation.published && !invitation.readyToPublish;
  // A published invitation keeps its publish control whatever a transaction is doing:
  // a refund can reverse the live transaction and leave a fresh pending one behind, and
  // the couple must still be able to take the page down.
  const settlingTransaction = invitation.transactionInProgress && !invitation.published;
  const paywallEligible = !invitation.published && !invitation.liveTransaction && !invitation.transactionInProgress;
  const publicationStatus = publicationStatusOf({
    published: invitation.published,
    settlingTransaction,
    paidAndReady: invitation.paidAndReady,
  });
  const [paywallOpen, setPaywallOpen] = React.useState(false);
  const { pending: resuming, start: resumeCheckout } = useCheckoutAction();

  return (
    <article
      aria-label={`${invitation.coupleNames} invitation`}
      data-invitation-card
      data-invitation-workspace
      className={cn(
        "min-w-0 overflow-hidden rounded-lg border bg-background shadow-sm",
        invitation.published && "border-primary/40",
      )}
    >
      <div className="grid min-w-0 lg:grid-cols-[14rem_minmax(0,1fr)] xl:grid-cols-[15rem_minmax(0,1fr)]">
        <div className="relative h-52 min-w-0 border-b bg-muted lg:h-full lg:min-h-[22rem] lg:border-r lg:border-b-0">
          <img
            src={invitation.themePreviewUrl}
            alt={`${invitation.coupleNames} theme preview`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover object-center"
          />
          <Button
            asChild
            variant="secondary"
            size="xs"
            className="absolute inset-x-3 bottom-3 border bg-background shadow-sm hover:bg-accent"
          >
            <a
              href={invitation.previewPath}
              target="_blank"
              rel="noreferrer"
              aria-label={`Preview ${invitation.coupleNames}`}
            >
              <EyeIcon aria-hidden="true" />
              Preview
            </a>
          </Button>
        </div>

        <div className="min-w-0">
          <div data-invitation-overview className="min-w-0 p-5 sm:p-6">
            <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                {invitation.weddingDate && (
                  <time className="block text-sm font-medium text-primary">
                    {invitation.weddingDate}
                  </time>
                )}
                <h2
                  className={cn(
                    "min-w-0 font-serif text-3xl font-semibold leading-tight tracking-tight [overflow-wrap:anywhere]",
                    invitation.weddingDate && "mt-1",
                  )}
                >
                  {invitation.coupleNames}
                </h2>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <Badge variant={publicationStatus.variant}>{publicationStatus.label}</Badge>
                {settlingTransaction ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    disabled={resuming}
                    onClick={() => resumeCheckout(invitation.checkoutPath)}
                    aria-label={`Resume checkout for ${invitation.coupleNames}`}
                  >
                    {resuming ? "Opening checkout…" : "Resume checkout"}
                  </Button>
                ) : (
                  <Form
                    {...invitation.togglePublicationForm.form}
                    extras={invitation.togglePublicationForm.extras}
                    data-sg-remote="true"
                    className="flex items-center"
                  >
                    <input {...invitation.togglePublicationForm.inputs.published} />
                    <Switch
                      defaultChecked={invitation.published}
                      disabled={!invitation.published && !invitation.readyToPublish}
                      aria-label={`${invitation.togglePublicationForm.inputs.submit.text} ${invitation.coupleNames}`}
                      aria-haspopup={paywallEligible ? "dialog" : undefined}
                      onClick={(event) => {
                        if (paywallEligible) {
                          event.preventDefault();
                          setPaywallOpen(true);
                          return;
                        }
                        event.currentTarget.closest("form")?.requestSubmit();
                      }}
                    />
                  </Form>
                )}
              </div>
            </div>

            <div className="mt-6 grid min-w-0 gap-6 md:grid-cols-[minmax(0,1fr)_15rem]">
              <div className="min-w-0">
                {settlingTransaction ? (
                  <div>
                    <p className="text-sm font-medium text-foreground">Payment in progress</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      We’re still waiting for your transaction to complete. Resume checkout to finish it —
                      you’ll publish the invitation yourself once it’s confirmed.
                    </p>
                  </div>
                ) : invitation.paidAndReady ? (
                  <div>
                    <p className="text-sm font-medium text-foreground">Payment complete</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Your invitation is ready to publish. Publishing costs nothing further.
                    </p>
                  </div>
                ) : invitation.lastTransactionFailed ? (
                  // Why the paywall is back. The provider's own code stays out
                  // of this: it is written for a merchant, and "do not honor"
                  // tells a couple nothing they can act on.
                  <div>
                    <p className="text-sm font-medium text-foreground">Payment declined</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Your last transaction attempt did not go through and nothing was charged. Publish again to try
                      another card or payment method.
                    </p>
                  </div>
                ) : !invitation.published && setupProgress ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
                      <span className="font-medium text-foreground">{setupProgress.progressLabel}</span>
                      <span className="text-muted-foreground">Next: {setupProgress.nextStep.label}</span>
                    </div>
                    <SetupProgressRail
                      steps={setupProgress.steps}
                      progressValue={setupProgress.progressValue}
                      ariaLabel={`Invitation setup for ${invitation.coupleNames}`}
                    />
                  </div>
                ) : !invitation.published && !invitation.readyToPublish ? (
                  <div>
                    <p className="text-sm font-medium text-foreground">Needs attention</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Finish the details that need attention before publishing.
                    </p>
                  </div>
                ) : (
                  <div data-invitation-readiness>
                    <p className="text-sm font-medium text-foreground">
                      {invitation.published ? "Live and ready for guests" : "Ready to publish"}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {invitation.published
                        ? "Your public link is open and collecting replies."
                        : "Your required details are complete. Publish whenever you’re ready."}
                    </p>
                  </div>
                )}
              </div>

              <div
                data-invitation-response-panel
                className="min-w-0 border-t pt-5 md:border-t-0 md:border-l md:pt-0 md:pl-6"
              >
                <p className="text-xs font-medium text-muted-foreground">Guest responses</p>
                {invitation.attendanceSummary.responseCount === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">No guests have responded yet</p>
                ) : (
                  <div data-attendance-summary className="mt-2">
                    <p className="text-sm text-muted-foreground">Expected headcount</p>
                    <p className="font-serif text-3xl font-semibold tabular-nums tracking-tight">
                      {invitation.attendanceSummary.expectedHeadcount}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <p>
                        <span className="font-semibold tabular-nums text-foreground">
                          {invitation.attendanceSummary.attendingCount}
                        </span>{" "}
                        attending
                      </p>
                      <p>
                        <span className="font-semibold tabular-nums text-foreground">
                          {invitation.attendanceSummary.notAttendingCount}
                        </span>{" "}
                        not attending
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button asChild className="w-full sm:w-auto">
                {needsAttention ? (
                  <a
                    href={setupProgress?.continueSetupPath || invitation.editPath}
                    data-sg-visit="true"
                    data-invitation-primary-action
                    aria-label={`Continue setting up ${invitation.coupleNames}`}
                  >
                    <PencilIcon aria-hidden="true" />
                    Continue setup
                  </a>
                ) : (
                  <a
                    href={invitation.editPath}
                    data-sg-visit="true"
                    data-invitation-primary-action
                    aria-label={`Edit ${invitation.coupleNames}`}
                  >
                    <PencilIcon aria-hidden="true" />
                    Edit
                  </a>
                )}
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <a
                  href={invitation.guestsPath}
                  data-sg-visit="true"
                  aria-label={`View guests for ${invitation.coupleNames}`}
                >
                  <UsersIcon aria-hidden="true" />
                  View guests
                </a>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <a
                  href={invitation.changeThemePath}
                  data-sg-visit="true"
                  aria-label={`Change theme for ${invitation.coupleNames}`}
                >
                  <PaletteIcon aria-hidden="true" />
                  Change theme
                </a>
              </Button>
            </div>
          </div>

          <div data-invitation-sharing-panel className="min-w-0 px-5 pb-5 sm:px-6 sm:pb-6">
            <Separator className="!border-b !border-border !border-dashed !bg-transparent" />
            <div className="grid min-w-0 gap-3 pt-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
              <div className="min-w-0">
                <p className="mb-2 text-xs font-medium text-muted-foreground">Public link</p>
                <InputGroup>
                  <InputGroupInput
                    aria-label={`Public link for ${invitation.coupleNames}`}
                    value={invitation.publicUrl}
                    readOnly
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      aria-label={`Copy ${invitation.coupleNames} link`}
                      disabled={!invitation.published}
                      onClick={() => onCopy(invitation.publicUrl)}
                    >
                      {copied ? (
                        <>
                          <CheckIcon aria-hidden="true" />
                          Copied
                        </>
                      ) : (
                        <>
                          <CopyIcon aria-hidden="true" />
                          Copy
                        </>
                      )}
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
              </div>

              <div className="flex flex-wrap gap-2">
                {invitation.published ? (
                  <Button asChild variant="outline" size="sm">
                    <a
                      href={invitation.publicUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${invitation.coupleNames} invitation`}
                    >
                      <ExternalLinkIcon aria-hidden="true" />
                      Open
                    </a>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled
                    aria-label={`Open ${invitation.coupleNames} invitation`}
                  >
                    <ExternalLinkIcon aria-hidden="true" />
                    Open
                  </Button>
                )}
                {invitation.published ? (
                  <Button asChild variant="outline" size="sm">
                    <a
                      href={invitation.whatsappShareUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Share ${invitation.coupleNames} invitation on WhatsApp`}
                    >
                      <MessageCircleIcon aria-hidden="true" />
                      WhatsApp
                    </a>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled
                    aria-label={`Share ${invitation.coupleNames} invitation on WhatsApp`}
                  >
                    <MessageCircleIcon aria-hidden="true" />
                    WhatsApp
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {paywallEligible && (
        <PaywallDialog invitation={invitation} open={paywallOpen} onOpenChange={setPaywallOpen} />
      )}
    </article>
  );
}

export default function Dashboard() {
  const {
    invitations,
    dashboardSummary,
    newInvitationPath,
    demoInvitationPath,
    confirmationPending,
    confirmationPath,
    welcome,
  } = useContent<DashboardContent>();
  const [copiedUrl, setCopiedUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!welcome) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "lead" });
  }, [welcome]);

  if (invitations.length === 0) {
    return (
      <div className="flex flex-1 items-center px-4 py-8 sm:px-6 sm:py-10 lg:py-12">
        <section data-dashboard-workbench className="mx-auto w-full max-w-7xl">
          <div className="grid min-w-0 overflow-hidden rounded-lg border bg-background shadow-sm lg:grid-cols-[minmax(0,1.15fr)_minmax(20rem,0.85fr)]">
            <Empty
              data-first-run-introduction
              className="items-start rounded-none border-0 p-6 text-left sm:p-10 lg:p-14"
            >
              <EmptyHeader className="max-w-2xl items-start text-left">
                <EmptyMedia variant="icon" aria-hidden="true">
                  <HeartIcon />
                </EmptyMedia>
                <EmptyTitle>
                  <TypographyH1 className="text-left">Your wedding invitation starts here</TypographyH1>
                </EmptyTitle>
                <EmptyDescription className="max-w-xl text-left">
                  Choose a theme, add the details your guests need, and publish one beautiful link where they can RSVP.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent className="max-w-none items-start">
                <CreateInvitationAction {...{ confirmationPending, confirmationPath, newInvitationPath }} />
                <Button asChild variant="outline">
                  <a
                    href={demoInvitationPath}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="See a live demo invitation"
                  >
                    <EyeIcon aria-hidden="true" />
                    Open live demo
                  </a>
                </Button>
              </EmptyContent>
            </Empty>

            <aside data-first-run-path className="border-t bg-muted/40 p-6 sm:p-10 lg:border-t-0 lg:border-l lg:p-12">
              <p className="text-xs font-medium text-muted-foreground">Your path to launch</p>
              <h2 className="mt-2 min-w-0 max-w-sm font-serif text-2xl font-semibold tracking-tight text-foreground [overflow-wrap:anywhere]">
                From first idea to guest-ready
              </h2>
              <ol className="mt-8">
                {firstRunSteps.map((step, index) => (
                  <li
                    key={step.title}
                    className="grid grid-cols-[2rem_minmax(0,1fr)] gap-4 border-t py-5 first:border-t-0 first:pt-0"
                  >
                    <span aria-hidden="true" className="font-serif text-sm tabular-nums text-primary">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-medium text-foreground">{step.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{step.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </aside>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="px-4 py-8 sm:px-6 sm:py-10 lg:py-12">
      <section data-dashboard-workbench className="mx-auto w-full max-w-7xl">
        <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 max-w-2xl">
            <TypographyH1 className="text-left">Your invitations</TypographyH1>
            <TypographyMuted className="mt-2">
              See what’s live, what needs attention, and how your guest list is taking shape.
            </TypographyMuted>
          </div>
          <CreateInvitationAction {...{ confirmationPending, confirmationPath, newInvitationPath }} />
        </header>

        <DashboardSummary summary={dashboardSummary} />

        <div className="mt-8 grid gap-6">
          {invitations.map((invitation) => (
            <InvitationWorkspace
              key={invitation.editPath}
              invitation={invitation}
              copied={copiedUrl === invitation.publicUrl}
              onCopy={async (url) => {
                await navigator.clipboard.writeText(url);
                setCopiedUrl(url);
              }}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
